import {
  IconChevronDown,
  IconFileInvoice,
  IconFileTypePdf,
  IconLoader2,
  IconSend,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  useGenerateDraftsMutation,
  useBulkSendEmailsMutation,
  useGetEmailAddressesQuery,
} from "../api/queries/paymentsQueries";
import { useEmailTemplatesQuery } from "../api/queries/emailTemplateQueries";
import { useAuth } from "../hooks/useAuth";
import { InvoiceBulkActionConfirmModal } from "./InvoiceBulkActionConfirmModal";

type BulkActionScope = "invoices" | "customers";

interface ReceivablesBulkActionsMenuProps {
  scope: BulkActionScope;
  selectedInvoiceIds?: string[];
  selectedCustomerIds?: number[];
  onViewFiles?: (invoiceIds: string[]) => void;
}

type BulkAction = "generate_drafts" | "send_emails";

const INVOICE_ACTION_OPTIONS: {
  id: BulkAction;
  title: string;
  description: string;
  icon: typeof IconFileInvoice;
}[] = [
  {
    id: "generate_drafts",
    title: "Generate emails",
    description: "Create drafts from your email templates",
    icon: IconFileInvoice,
  },
  {
    id: "send_emails",
    title: "Send emails",
    description: "Send drafts from your connected mailbox",
    icon: IconSend,
  },
];

export const ReceivablesBulkActionsMenu = ({
  scope,
  selectedInvoiceIds = [],
  selectedCustomerIds = [],
  onViewFiles,
}: ReceivablesBulkActionsMenuProps) => {
  const { canMutateOrg } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<BulkAction | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const generateDraftsMutation = useGenerateDraftsMutation();
  const bulkSendEmailsMutation = useBulkSendEmailsMutation();
  const emailTemplatesQuery = useEmailTemplatesQuery();
  const hasTemplates = (emailTemplatesQuery.data ?? []).length > 0;

  const selectedIds =
    scope === "customers" ? selectedCustomerIds : selectedInvoiceIds;

  const emailAddressesQuery = useGetEmailAddressesQuery(
    pendingAction === "send_emails"
      ? scope === "customers"
        ? { customer_organization_ids: selectedCustomerIds }
        : { payment_external_ids: selectedInvoiceIds }
      : null,
    pendingAction === "send_emails" && confirmModalOpen,
  );

  const hasSelection = selectedIds.length > 0;
  const isLoading =
    generateDraftsMutation.isPending || bulkSendEmailsMutation.isPending;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  if (!canMutateOrg) return null;

  const openConfirmModal = (action: BulkAction) => {
    setPendingAction(action);
    setConfirmModalOpen(true);
    setMenuOpen(false);
  };

  const handleConfirm = async (includeInvoicePdfs?: boolean) => {
    if (pendingAction === "generate_drafts") {
      const totalCount = selectedIds.length;
      const loadingToast = toast.loading(
        scope === "customers"
          ? `Generating drafts for ${totalCount} customer${totalCount === 1 ? "" : "s"}…`
          : `Generating drafts for ${totalCount} invoice${totalCount === 1 ? "" : "s"}…`,
      );

      try {
        const payload =
          scope === "customers"
            ? { customer_organization_ids: selectedCustomerIds }
            : { payment_external_ids: selectedInvoiceIds };
        const result = await generateDraftsMutation.mutateAsync(payload);

        toast.dismiss(loadingToast);
        const message = `Generated ${result.generated_count} draft(s). ${result.skipped_count} skipped, ${result.failed_count} failed.`;

        if (result.failed_count > 0 || result.generated_count === 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
      } catch (error: unknown) {
        toast.dismiss(loadingToast);

        const err = error as {
          code?: string;
          message?: string;
          response?: { status?: number; data?: { detail?: unknown } };
        };

        if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
          toast.error(
            "Request timed out. The operation may still be processing. Please check back in a few moments.",
          );
        } else if (
          err?.response?.status === 504 ||
          err?.response?.status === 408
        ) {
          toast.error(
            "Request timed out. Please try again or contact support if the issue persists.",
          );
        } else {
          const detail = err?.response?.data?.detail;
          const errorMessage =
            (typeof detail === "object" &&
            detail !== null &&
            "message" in detail &&
            typeof (detail as { message?: string }).message === "string"
              ? (detail as { message: string }).message
              : typeof detail === "string"
                ? detail
                : null) ||
            err?.message ||
            "Failed to generate drafts";
          toast.error(`Failed to generate drafts: ${errorMessage}`);
        }
        console.error("Generate drafts error:", error);
      }
    } else if (pendingAction === "send_emails") {
      const invoicesWithDrafts =
        emailAddressesQuery.data?.invoices_with_drafts || [];

      if (invoicesWithDrafts.length === 0) {
        toast.info(
          scope === "customers"
            ? "No open invoices have drafts. Please generate drafts first."
            : "No selected invoices have drafts. Please generate drafts first.",
        );
        setConfirmModalOpen(false);
        setPendingAction(null);
        return;
      }

      const totalCount = invoicesWithDrafts.length;
      const loadingToast = toast.loading(
        `Sending ${totalCount} email${totalCount === 1 ? "" : "s"}…`,
      );

      try {
        const result = await bulkSendEmailsMutation.mutateAsync({
          payment_external_ids: invoicesWithDrafts,
          includeInvoicePdfs: includeInvoicePdfs || false,
        });

        toast.dismiss(loadingToast);
        const successCount = result.successful.length;
        const failCount = result.failed.length;
        const skippedCount = result.skipped?.length || 0;

        if (successCount > 0 && failCount === 0 && skippedCount === 0) {
          toast.success(
            `Successfully sent ${successCount} email(s) out of ${totalCount}`,
          );
        } else if (successCount > 0 && (failCount > 0 || skippedCount > 0)) {
          const parts: string[] = [];
          if (successCount > 0) parts.push(`${successCount} sent`);
          if (skippedCount > 0)
            parts.push(`${skippedCount} skipped (credit invoice)`);
          if (failCount > 0) parts.push(`${failCount} failed`);
          toast.warning(`${parts.join(", ")} out of ${totalCount}`);
        } else if (skippedCount > 0 && successCount === 0 && failCount === 0) {
          toast.info(
            `${skippedCount} email(s) skipped: Customer has a credit invoice`,
          );
        } else {
          toast.error(`Failed to send all ${totalCount} email(s)`);
        }
      } catch (error) {
        toast.dismiss(loadingToast);
        toast.error("Failed to send emails");
        console.error("Send emails error:", error);
      }
    }
    setConfirmModalOpen(false);
    setPendingAction(null);
  };

  const handleCancel = () => {
    setConfirmModalOpen(false);
    setPendingAction(null);
  };

  const totalOpenInvoicesForSend =
    (emailAddressesQuery.data?.invoices_with_drafts.length ?? 0) +
    (emailAddressesQuery.data?.invoices_without_drafts.length ?? 0);

  const getActionCount = () => {
    if (pendingAction === "generate_drafts") {
      return selectedIds.length;
    }
    if (pendingAction === "send_emails") {
      return emailAddressesQuery.data?.invoices_with_drafts.length || 0;
    }
    return 0;
  };

  const pendingMutation =
    pendingAction === "generate_drafts"
      ? generateDraftsMutation.isPending
      : pendingAction === "send_emails"
        ? bulkSendEmailsMutation.isPending
        : false;

  const selectionLabel =
    scope === "customers"
      ? `${selectedIds.length} customer${selectedIds.length === 1 ? "" : "s"} selected`
      : `${selectedIds.length} invoice${selectedIds.length === 1 ? "" : "s"} selected`;

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          disabled={!hasSelection || isLoading}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
            hasSelection
              ? "border-[#4B7F73]/30 bg-[#EEF4F2] text-[#3F6C61] hover:bg-[#E4EEEA]"
              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          {isLoading ? (
            <IconLoader2 size={16} className="animate-spin" />
          ) : null}
          Actions
          {hasSelection ? (
            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-[#4B7F73] px-1.5 py-0.5 text-xs font-semibold text-white">
              {selectedIds.length}
            </span>
          ) : null}
          <IconChevronDown
            size={14}
            className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}
          />
        </button>

        {menuOpen && hasSelection ? (
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
          >
            <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Bulk actions
              </p>
              <p className="mt-1 text-sm text-slate-600">{selectionLabel}</p>
            </div>

            <div className="p-2">
              {scope === "invoices" && onViewFiles ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onViewFiles(selectedInvoiceIds);
                    setMenuOpen(false);
                  }}
                  className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <IconFileTypePdf size={16} stroke={1.75} />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-slate-900">
                      View files
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Preview invoice PDFs in a tabbed viewer
                    </span>
                  </span>
                </button>
              ) : null}

              {scope === "invoices" && onViewFiles ? (
                <div className="my-1 border-t border-slate-100" />
              ) : null}

              <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                Email actions
              </p>
              {INVOICE_ACTION_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isPending =
                  (option.id === "generate_drafts" &&
                    generateDraftsMutation.isPending) ||
                  (option.id === "send_emails" &&
                    bulkSendEmailsMutation.isPending);

                return (
                  <button
                    key={option.id}
                    type="button"
                    role="menuitem"
                    disabled={isLoading}
                    onClick={() => openConfirmModal(option.id)}
                    className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <span
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                        option.id === "generate_drafts"
                          ? "bg-[#EEF4F2] text-[#4B7F73]"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {isPending ? (
                        <IconLoader2 size={16} className="animate-spin" />
                      ) : (
                        <Icon size={16} stroke={1.75} />
                      )}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-slate-900">
                        {isPending
                          ? option.id === "generate_drafts"
                            ? "Generating…"
                            : "Sending…"
                          : option.title}
                      </span>
                      <span className="mt-0.5 block text-xs text-slate-500">
                        {scope === "customers" && option.id === "generate_drafts"
                          ? "Create drafts for all open invoices of selected customers"
                          : scope === "customers" && option.id === "send_emails"
                            ? "Send drafts for all open invoices of selected customers"
                            : option.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>

      <InvoiceBulkActionConfirmModal
        action={pendingAction || "generate_drafts"}
        scope={scope}
        selectedCount={getActionCount()}
        customersSelectedCount={
          pendingAction === "send_emails" && scope === "customers"
            ? selectedCustomerIds.length
            : undefined
        }
        totalSelectedCount={
          pendingAction === "send_emails" ? totalOpenInvoicesForSend : undefined
        }
        invoicesWithoutDraftsCount={
          pendingAction === "send_emails"
            ? emailAddressesQuery.data?.invoices_without_drafts.length || 0
            : 0
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isOpen={confirmModalOpen}
        isLoading={pendingMutation}
        isFetchingDetails={
          pendingAction === "send_emails" && emailAddressesQuery.isLoading
        }
        emailAddresses={
          pendingAction === "send_emails"
            ? emailAddressesQuery.data?.email_addresses
            : undefined
        }
        hasTemplates={hasTemplates}
      />
    </>
  );
};

/** @deprecated Use ReceivablesBulkActionsMenu */
export const InvoiceBulkActionsMenu = ({
  selectedIds,
  onViewFiles,
}: {
  selectedIds: string[];
  onViewFiles?: (invoiceIds: string[]) => void;
}) => (
  <ReceivablesBulkActionsMenu
    scope="invoices"
    selectedInvoiceIds={selectedIds}
    onViewFiles={onViewFiles}
  />
);
